/**
 * justHi backend API client.
 *
 * Slim wrapper sobre axios para los endpoints del backend FastAPI.
 * Estructurado por dominio (authApi, usersApi, ...) como fleethub, pero
 * solo expone lo necesario para el admin V1.
 */
import apiClient from './axios';

// ============================================================================
// SHARED TYPES & UTILITIES
// ============================================================================

export interface PaginationQuery {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  [key: string]: unknown;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

function buildParams(query?: PaginationQuery): string {
  if (!query) return '';
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const str = params.toString();
  return str ? `?${str}` : '';
}

// ============================================================================
// AUTH API
// ============================================================================

export interface AuthUser {
  id: string;
  email: string | null;
  phone: string | null;
  status: string;
  primary_role_slug: string;
  /** Derivado de primary_role_slug === 'super_admin'. Gates UI estilo fleethub. */
  isPlatformAdmin?: boolean;
  email_verified_at: string | null;
  phone_verified_at: string | null;
  created_at: string;
  // ─── Compat fleethub: campos que el codigo heredado del frontend admin
  //     espera pero justHi backend no devuelve. Todos optional para que
  //     TypeScript no rompa la compilacion; en runtime quedan undefined
  //     y los componentes degradan con fallbacks (avatar inicial, etc). ─────
  name?: string;
  avatar?: string | null;
  avatarUrl?: string | null;
  googleId?: string | null;
  isActive?: boolean;
  tenantId?: string | null;
  tenant?: {
    id: string;
    name: string;
    slug: string;
    isActive?: boolean;
    [key: string]: unknown;
  } | null;
  // Otras props legacy que algun componente pueda leer (timestamps, role, etc).
  createdAt?: string;
  updatedAt?: string;
  roles?: Array<{ role: { id: string; name: string; label?: string } }>;
}

export interface OtpRequestResponse {
  otp_token: string;
  expires_in: number;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: 'bearer';
  expires_in: number;
}

export const authApi = {
  /** Admin login (factor 1): email+password → otp_token (email OTP sent). */
  async adminLogin(payload: { email: string; password: string }): Promise<OtpRequestResponse> {
    const { data } = await apiClient.post<OtpRequestResponse>('/auth/admin/login', payload);
    return data;
  },

  /** Mobile login (OTP-only): email|phone → otp_token. */
  async login(payload: { email?: string; phone?: string }): Promise<OtpRequestResponse> {
    const { data } = await apiClient.post<OtpRequestResponse>('/auth/login', payload);
    return data;
  },

  /** Verifica el OTP del paso 2 → tokens. */
  async verify(payload: { otp_token: string; code: string }): Promise<AuthTokens> {
    const { data } = await apiClient.post<AuthTokens>('/auth/verify', payload);
    return data;
  },

  /** Obtiene el perfil del usuario actual (Bearer requerido). */
  async me(): Promise<AuthUser> {
    const { data } = await apiClient.get<AuthUser>('/auth/me');
    return data;
  },

  /** Refresh manual (los interceptors lo hacen automáticamente). */
  async refresh(refreshToken: string): Promise<AuthTokens> {
    const { data } = await apiClient.post<AuthTokens>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return data;
  },

  /** Logout single device. */
  async logout(): Promise<void> {
    await apiClient.post('/auth/logout', {}).catch(() => undefined);
  },

  /** Logout todos los devices del user. */
  async logoutAll(): Promise<void> {
    await apiClient.post('/auth/logout/all', {});
  },
};

// ============================================================================
// USERS API (stubs V1 — endpoints reales se añaden cuando existan)
// ============================================================================

export interface UserListItem {
  id: string;
  email: string | null;
  phone: string | null;
  status: string;
  primary_role_slug: string;
  created_at: string;
}

export const usersApi = {
  async list(query?: PaginationQuery): Promise<PaginatedResponse<UserListItem>> {
    const { data } = await apiClient.get<PaginatedResponse<UserListItem>>(
      `/users${buildParams(query)}`,
    );
    return data;
  },

  async getAll(query?: PaginationQuery): Promise<PaginatedResponse<UserListItem>> {
    return usersApi.list(query);
  },

  async get(id: string): Promise<UserListItem> {
    const { data } = await apiClient.get<UserListItem>(`/users/${id}`);
    return data;
  },

  async create(payload: {
    email: string;
    name?: string;
    password?: string;
    roleIds?: string[];
  }): Promise<UserListItem> {
    const { data } = await apiClient.post<UserListItem>('/users', payload);
    return data;
  },

  async update(
    id: string,
    payload: {
      email?: string;
      name?: string;
      password?: string;
      roleIds?: string[];
      isActive?: boolean;
    },
  ): Promise<UserListItem> {
    const { data } = await apiClient.patch<UserListItem>(`/users/${id}`, payload);
    return data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },

  async suspend(id: string): Promise<void> {
    await apiClient.post(`/users/${id}/suspend`);
  },

  async restore(id: string): Promise<void> {
    await apiClient.post(`/users/${id}/restore`);
  },

  async getProfile(): Promise<AuthUser> {
    return authApi.me();
  },

  async impersonate(_userId: string): Promise<never> {
    throw new Error('Impersonation not implemented in justHi V1');
  },

  // Stub: avatar upload no implementado en justHi V1. La UI del dashboard
  // referencia el endpoint pero la feature esta inactiva. Devuelve la shape
  // esperada para que TS valide los callers; el handler `onError` muestra
  // toast al usuario.
  async uploadAvatar(_file: File): Promise<{ avatar: string }> {
    return stubReject('usersApi.uploadAvatar');
  },
};

// ============================================================================
// PLACES API (admin CRUD — endpoint /admin/places)
// ============================================================================

/** @deprecated Categorías ahora son dinámicas (tabla `categories`). El tipo se
 *  mantiene como alias `string` para no romper consumidores legacy. */
export type PlaceCategory = string;

export type PlaceStatus = 'provisional' | 'verified' | 'blocked';

export interface PlaceItem {
  id: string;
  name: string;
  category: string;
  description: string | null;
  lat: number;
  lng: number;
  address_line: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  website: string | null;
  status: PlaceStatus;
  checkins_total: number;
  cover_image_key: string | null;
  coverUrl: string | null;
  createdAt: string;
}

export interface PlaceCreatePayload {
  name: string;
  category: string;
  description?: string | null;
  lat: number;
  lng: number;
  address_line?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  website?: string | null;
}

export type PlaceUpdatePayload = Partial<PlaceCreatePayload & { status: PlaceStatus }>;

export const placesApi = {
  async list(query?: PaginationQuery): Promise<PaginatedResponse<PlaceItem>> {
    const { data } = await apiClient.get<PaginatedResponse<PlaceItem>>(
      `/admin/places${buildParams(query)}`,
    );
    return data;
  },

  async getAll(query?: PaginationQuery): Promise<PaginatedResponse<PlaceItem>> {
    return placesApi.list(query);
  },

  async getOne(id: string): Promise<PlaceItem> {
    const { data } = await apiClient.get<PlaceItem>(`/admin/places/${id}`);
    return data;
  },

  async create(payload: PlaceCreatePayload): Promise<PlaceItem> {
    const { data } = await apiClient.post<PlaceItem>('/admin/places', payload);
    return data;
  },

  async update(id: string, payload: PlaceUpdatePayload): Promise<PlaceItem> {
    const { data } = await apiClient.patch<PlaceItem>(`/admin/places/${id}`, payload);
    return data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/admin/places/${id}`);
  },
};

// ============================================================================
// MOMENTS API (admin CRUD — endpoint /admin/moments)
// ============================================================================

export type MomentStatus = 'provisional' | 'verified' | 'blocked';

export interface MomentItem {
  id: string;
  name: string;
  category: string;
  description: string | null;
  lat: number;
  lng: number;
  starts_at: string;
  ends_at: string;
  organizer_name: string | null;
  organizer_url: string | null;
  expected_attendees: number | null;
  status: MomentStatus;
  checkins_total: number;
  cover_image_key: string | null;
  coverUrl: string | null;
  createdAt: string;
}

export interface MomentCreatePayload {
  name: string;
  category: string;
  description?: string | null;
  lat: number;
  lng: number;
  starts_at: string;
  ends_at: string;
  organizer_name?: string | null;
  organizer_url?: string | null;
  expected_attendees?: number | null;
}

export type MomentUpdatePayload = Partial<MomentCreatePayload & { status: MomentStatus }>;

export const momentsApi = {
  async list(query?: PaginationQuery): Promise<PaginatedResponse<MomentItem>> {
    const { data } = await apiClient.get<PaginatedResponse<MomentItem>>(
      `/admin/moments${buildParams(query)}`,
    );
    return data;
  },

  async getAll(query?: PaginationQuery): Promise<PaginatedResponse<MomentItem>> {
    return momentsApi.list(query);
  },

  async getOne(id: string): Promise<MomentItem> {
    const { data } = await apiClient.get<MomentItem>(`/admin/moments/${id}`);
    return data;
  },

  async create(payload: MomentCreatePayload): Promise<MomentItem> {
    const { data } = await apiClient.post<MomentItem>('/admin/moments', payload);
    return data;
  },

  async update(id: string, payload: MomentUpdatePayload): Promise<MomentItem> {
    const { data } = await apiClient.patch<MomentItem>(`/admin/moments/${id}`, payload);
    return data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/admin/moments/${id}`);
  },
};

// ============================================================================
// CATEGORIES API (catalogo dinamico — admin CRUD + dropdowns)
// ============================================================================

export type CategoryAppliesTo = 'place' | 'moment' | 'both';

export interface CategoryItem {
  id: string;
  slug: string;
  label: string;
  applies_to: CategoryAppliesTo;
  icon: string | null;
  color: string | null;
  sort_order: number;
  is_system: boolean;
  is_active: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface CategoryCreatePayload {
  slug: string;
  label: string;
  applies_to: CategoryAppliesTo;
  icon?: string | null;
  color?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

export type CategoryUpdatePayload = Partial<CategoryCreatePayload>;

export const categoriesApi = {
  async list(query?: PaginationQuery & { applies_to?: CategoryAppliesTo; is_active?: boolean }): Promise<PaginatedResponse<CategoryItem>> {
    const { data } = await apiClient.get<PaginatedResponse<CategoryItem>>(
      `/admin/categories${buildParams(query)}`,
    );
    return data;
  },

  async getAll(query?: PaginationQuery & { applies_to?: CategoryAppliesTo; is_active?: boolean }): Promise<PaginatedResponse<CategoryItem>> {
    return categoriesApi.list(query);
  },

  async getOne(id: string): Promise<CategoryItem> {
    const { data } = await apiClient.get<CategoryItem>(`/admin/categories/${id}`);
    return data;
  },

  async create(payload: CategoryCreatePayload): Promise<CategoryItem> {
    const { data } = await apiClient.post<CategoryItem>('/admin/categories', payload);
    return data;
  },

  async update(id: string, payload: CategoryUpdatePayload): Promise<CategoryItem> {
    const { data } = await apiClient.patch<CategoryItem>(`/admin/categories/${id}`, payload);
    return data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/admin/categories/${id}`);
  },

  /** Carga opciones activas para `applies_to` (incluye 'both'). Cacheable en
   *  React Query: ideal para los dropdowns de PlacePageForm / MomentPageForm. */
  async listOptions(appliesTo: 'place' | 'moment'): Promise<CategoryItem[]> {
    const res = await categoriesApi.list({
      applies_to: appliesTo,
      is_active: true,
      limit: 5000,
      sortBy: 'sort_order',
      sortOrder: 'asc',
    });
    return res.data;
  },
};

// ============================================================================
// PLATFORM API (no aplica en justHi V1 — no es multi-tenant)
// ============================================================================

export const platformApi = {
  /** Lista de tenants. justHi no es multi-tenant: devolvemos uno fake "justHi"
   * para que el frontend (heredado de fleethub multi-tenant) tenga algo
   * con lo que rellenar selectores. */
  async getTenants(_options?: { includeInactive?: boolean }): Promise<Array<{ id: string; name: string; slug: string; isActive: boolean }>> {
    return [{ id: 'justhi', name: 'justHi', slug: 'justhi', isActive: true }];
  },
  async impersonateTenant(_tenantId: string): Promise<never> {
    throw new Error('Platform impersonation not applicable in justHi (no multi-tenant)');
  },
};

// ============================================================================
// ROLES / PERMISSIONS (stubs para system/roles UI)
// ============================================================================

export interface RoleDto {
  id: string;
  slug: string;
  label: string;
  priority: number;
  is_built_in: boolean;
}

export interface PermissionDto {
  id: string;
  slug: string;
  label: string;
  group: string;
}

export const rolesApi = {
  async list(): Promise<RoleDto[]> {
    const { data } = await apiClient.get<RoleDto[]>('/roles');
    return data;
  },

  async listRoles(_tenantId?: string): Promise<RoleDto[]> {
    // justHi: ignoramos tenantId (no es multi-tenant). El backend siempre
    // devuelve la lista canonica de roles.
    return rolesApi.list();
  },

  async listRoleOptions(): Promise<RoleDto[]> {
    const { data } = await apiClient.get<RoleDto[]>('/roles/options');
    return data;
  },

  async getFeatures(): Promise<FeatureMeta[]> {
    const { data } = await apiClient.get<FeatureMeta[]>('/roles/features');
    return data;
  },

  async getMyPermissions(): Promise<PermissionsMap> {
    const { data } = await apiClient.get<PermissionsMap>('/roles/permissions/me');
    return data;
  },

  async getAllPermissions(): Promise<PermissionDto[]> {
    return permissionsApi.list();
  },
};

export const permissionsApi = {
  async list(): Promise<PermissionDto[]> {
    const { data } = await apiClient.get<PermissionDto[]>('/roles/permissions');
    return data;
  },
};

// ============================================================================
// STUBS — APIs heredadas de fleethub que las pages restauradas (base-datos,
// mantenimiento, syncs, history, notificaciones, users, roles) referencian.
//
// V1 justHi: cada metodo lanza "endpoint not available" si se invoca contra
// un backend que no lo implementa. Las pages renderizan su UI pero muestran
// estado de error/empty al llamar; a medida que se implementen los endpoints
// en backend FastAPI se sustituyen por llamadas reales.
//
// Convencion: stub_X(method) => Promise.reject con mensaje accionable.
// ============================================================================

function stubReject(name: string): Promise<never> {
  return Promise.reject(
    Object.assign(new Error(`API not implemented in justHi V1: ${name}`), {
      response: { status: 501, data: { detail: `Not implemented: ${name}` } },
    }),
  );
}

// ─── Permission types (fleethub legacy) ───────────────────────────────────

export type PermissionAction =
  | 'canView'
  | 'canCreate'
  | 'canEdit'
  | 'canDelete'
  | 'canApprove'
  | 'canIssue'
  | 'canExecute';

export type PermissionFlags = Record<PermissionAction, boolean>;
export type PermissionsMap = Record<string, PermissionFlags>;
export interface FeatureMeta {
  feature: string;
  label: string;
  actions: PermissionAction[];
}

// ─── Database (system/base-datos) ────────────────────────────────────────

export interface DbTable {
  schema: string;
  table_name: string;
  row_count: number;
}

export interface DbColumn {
  column_name: string;
  data_type: string;
  is_nullable: boolean;
  default: string | null;
  max_length: number | null;
}

export interface DbQueryResult {
  columns: string[];
  rows: Array<Record<string, unknown>>;
  rowCount: number;
  type?: string;
  duration?: number;
  duration_ms?: number;
  error?: string;
}

export const databaseApi = {
  async listTables(): Promise<DbTable[]> {
    const { data } = await apiClient.get<DbTable[]>('/database/tables');
    return data;
  },
  async getColumns(table: string): Promise<DbColumn[]> {
    const { data } = await apiClient.get<DbColumn[]>(`/database/tables/columns?table=${table}`);
    return data;
  },
  async getTableData(
    table: string,
    page: number = 1,
    limit: number = 50,
  ): Promise<{ data: Array<Record<string, unknown>>; total: number; limit: number; offset: number }> {
    const offset = Math.max(0, (page - 1) * limit);
    const { data } = await apiClient.get(
      `/database/tables/data?table=${encodeURIComponent(table)}&limit=${limit}&offset=${offset}`,
    );
    return data;
  },
  async executeQuery(sql: string, readOnly = true): Promise<DbQueryResult> {
    const { data } = await apiClient.post<DbQueryResult>('/database/query', {
      sql,
      read_only: readOnly,
    });
    return data;
  },
};

// ─── Maintenance (system/mantenimiento) ──────────────────────────────────

export interface MaintenanceLogEntry {
  id: string;
  source: string;
  timestamp: string;
  level: string;
  subject: string;
  message: string;
  data: Record<string, unknown> | null;
}

export const maintenanceApi = {
  async getLogHistory(
    query?: PaginationQuery,
  ): Promise<{ data: MaintenanceLogEntry[]; total: number; totalPages: number; page: number; limit: number }> {
    const items = (await apiClient.get<MaintenanceLogEntry[]>(
      `/maintenance/logs/history${buildParams(query)}`,
    )).data;
    const page = Number(query?.page) || 1;
    const limit = Number(query?.limit) || 50;
    return {
      data: items,
      total: items.length,
      totalPages: Math.max(1, Math.ceil(items.length / limit)),
      page,
      limit,
    };
  },
};

// ─── Audit / event-logs (system/history) ─────────────────────────────────
// Shape 1:1 con fleethub: el backend justHi (admin_panel.audit_router)
// devuelve los campos camelCase + snake_case aliases (ver
// _audit_entry_to_dict en admin_panel/router.py). HistoryView consume los
// campos camelCase.

export type AuditAction =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'RESTORE'
  | 'STATUS_CHANGE'
  | 'PHOTO_ADDED'
  | 'VERIFICATION_COMPLETED'
  | 'NOTE_ADDED'
  | 'ASSIGNMENT_DELIVERED'
  | 'ASSIGNMENT_RETURNED'
  | 'CUSTOM';

export interface AuditChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface AuditEntry {
  id: string;
  action: AuditAction;
  createdAt: string;
  user: { id: string | null; name: string; email: string | null; avatar?: string | null } | null;
  changes: AuditChange[] | null;
  metadata: Record<string, unknown> | null;
}

export interface AuditListEntry extends AuditEntry {
  entityType: string;
  entityId: string | null;
}

export interface AuditTimelineResponse {
  items: AuditEntry[];
  nextCursor: string | null;
}

export interface AuditListParams {
  limit?: number;
  cursor?: string;
  entityType?: string;
  actions?: AuditAction[];
  userId?: string;
  search?: string;
  from?: string;
  to?: string;
}

export interface AuditListResponse {
  items: AuditListEntry[];
  nextCursor: string | null;
}

function _auditParams(params: AuditListParams): string {
  const sp = new URLSearchParams();
  if (params.limit) sp.set('limit', String(params.limit));
  if (params.cursor) sp.set('cursor', params.cursor);
  if (params.entityType) sp.set('entityType', params.entityType);
  if (params.userId) sp.set('userId', params.userId);
  if (params.actions) params.actions.forEach((a) => sp.append('actions', a));
  if (params.search) sp.set('search', params.search);
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  return sp.toString();
}

export const auditApi = {
  async list(params: AuditListParams = {}): Promise<AuditListResponse> {
    const qs = _auditParams(params);
    const { data } = await apiClient.get<AuditListResponse>(`/audit${qs ? `?${qs}` : ''}`);
    return data;
  },
  async listSystem(params: AuditListParams = {}): Promise<AuditListResponse> {
    const qs = _auditParams(params);
    const { data } = await apiClient.get<AuditListResponse>(`/audit/system${qs ? `?${qs}` : ''}`);
    return data;
  },
  async getEntityTypes(): Promise<{ entityTypes: string[] }> {
    const { data } = await apiClient.get<{ entityTypes: string[] }>('/audit/entity-types');
    return data;
  },
  async getSystemEntityTypes(): Promise<{ entityTypes: string[] }> {
    const { data } = await apiClient.get<{ entityTypes: string[] }>('/audit/system/entity-types');
    return data;
  },
};

// ─── Notifications (system/notificaciones + bell+panel) ──────────────────

export type NotificationSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export interface NotificationItem {
  id: string;
  userId: string;
  eventType: string;
  title: string;
  body: string | null;
  severity: NotificationSeverity;
  data: Record<string, unknown>;
  isRead: boolean;
  readAt: string | null;
  channels: Record<string, string>;
  createdAt: string;
}

export interface NotificationListResponse {
  data: NotificationItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface NotificationPreference {
  eventType: string;
  channel: string;
  enabled: boolean;
}

export const notificationsApi = {
  // ── User-facing (inbox + bell+panel) ─────────────────────────────────
  async list(query: PaginationQuery & { unreadOnly?: boolean } = {}): Promise<NotificationListResponse> {
    const { data } = await apiClient.get<NotificationListResponse>('/notifications', { params: query });
    return data;
  },
  async getAll(query: PaginationQuery & { unreadOnly?: boolean } = {}): Promise<NotificationListResponse> {
    const { data } = await apiClient.get<NotificationListResponse>('/notifications', { params: query });
    return data;
  },
  async unreadCount(): Promise<{ count: number }> {
    const { data } = await apiClient.get<{ count: number }>('/notifications/unread-count');
    return data;
  },
  async markAsRead(id: string): Promise<NotificationItem> {
    const { data } = await apiClient.patch<NotificationItem>(`/notifications/${id}/read`);
    return data;
  },
  async markRead(id: string): Promise<NotificationItem> {
    return notificationsApi.markAsRead(id);
  },
  async markAllAsRead(): Promise<void> {
    await apiClient.patch('/notifications/read-all');
  },
  async markAllRead(): Promise<void> {
    await notificationsApi.markAllAsRead();
  },
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/notifications/${id}`);
  },
  async deleteMany(ids: string[]): Promise<void> {
    await apiClient.post('/notifications/bulk-delete', { ids });
  },

  // ── Preferences ──────────────────────────────────────────────────────
  async getPreferences(): Promise<NotificationPreference[]> {
    const { data } = await apiClient.get<NotificationPreference[]>('/notifications/preferences');
    return data;
  },
  async updatePreferences(preferences: NotificationPreference[]): Promise<void> {
    await apiClient.put('/notifications/preferences', { preferences });
  },
  // alias legacy
  async preferences(): Promise<NotificationPreference[]> {
    return notificationsApi.getPreferences();
  },

  // ── Admin: types CRUD ────────────────────────────────────────────────
  async adminListTypes(query: Record<string, unknown> = {}): Promise<{ data: Array<Record<string, unknown>>; meta: { total: number; page: number; limit: number; totalPages: number } }> {
    const { data } = await apiClient.get('/notifications/admin/types', { params: query });
    return data;
  },
  async adminCreateType(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const { data } = await apiClient.post('/notifications/admin/types', payload);
    return data;
  },
  async adminUpdateType(id: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const { data } = await apiClient.patch(`/notifications/admin/types/${id}`, payload);
    return data;
  },
  async adminDeleteType(id: string): Promise<void> {
    await apiClient.delete(`/notifications/admin/types/${id}`);
  },

  // ── Admin: rules CRUD ────────────────────────────────────────────────
  async adminListRules(query: Record<string, unknown> = {}): Promise<{ data: Array<Record<string, unknown>>; meta: { total: number; page: number; limit: number; totalPages: number } }> {
    const { data } = await apiClient.get('/notifications/admin/rules', { params: query });
    return data;
  },
  async adminCreateRule(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const { data } = await apiClient.post('/notifications/admin/rules', payload);
    return data;
  },
  async adminUpdateRule(id: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const { data } = await apiClient.patch(`/notifications/admin/rules/${id}`, payload);
    return data;
  },
  async adminDeleteRule(id: string): Promise<void> {
    await apiClient.delete(`/notifications/admin/rules/${id}`);
  },

  // ── Catalog + send-test ──────────────────────────────────────────────
  async adminGetEventsCatalog(): Promise<Array<Record<string, unknown>>> {
    const { data } = await apiClient.get('/notifications/event-types');
    return data;
  },
  async adminSendTest(payload: Record<string, unknown>): Promise<{ id: string; delivered: boolean }> {
    const { data } = await apiClient.post('/notifications/admin/send-test', payload);
    return data;
  },

  // ── Web Push ─────────────────────────────────────────────────────────
  async pushPublicKey(): Promise<{ publicKey: string }> {
    const { data } = await apiClient.get<{ publicKey: string }>('/notifications/push/public-key');
    return data;
  },
  async pushListSubscriptions(): Promise<Array<{ id: string; endpoint: string; userAgent: string | null; createdAt: string }>> {
    const { data } = await apiClient.get('/notifications/push/subscriptions');
    return data;
  },
  async pushSubscribe(payload: { endpoint: string; keys: { p256dh: string; auth: string }; userAgent?: string }): Promise<unknown> {
    const { data } = await apiClient.post('/notifications/push/subscribe', payload);
    return data;
  },
  async pushUnsubscribe(endpoint: string): Promise<void> {
    await apiClient.delete('/notifications/push/subscribe', { params: { endpoint } });
  },
};

// ─── Dev tools (Expo tunnel info, V1 DEV only) ───────────────────────────

export interface ExpoInfo {
  host_uri: string | null;
  exp_url: string | null;
  running: boolean;
  sdk_version: string | null;
  mode?: 'nginx-https' | 'tunnel' | null;
}

export interface BuildMobileStatus {
  state: 'idle' | 'running' | 'success' | 'error';
  started_at?: string | null;
  finished_at?: string | null;
  apk_name?: string | null;
  message?: string | null;
  log_path?: string | null;
  /** 0-100, sólo presente cuando `state === 'running'` o `success`. */
  progress_pct?: number | null;
  /** Texto corto descriptivo de la fase actual (ej. "Compilando código"). */
  current_step?: string | null;
}

export const devApi = {
  async getExpoInfo(): Promise<ExpoInfo> {
    const { data } = await apiClient.get<ExpoInfo>('/admin/dev/expo-info');
    return data;
  },
  async buildMobile(platform: 'android' | 'ios'): Promise<{ ok: boolean; platform: string }> {
    const { data } = await apiClient.post<{ ok: boolean; platform: string }>(
      '/admin/dev/build-mobile',
      { platform },
    );
    return data;
  },
  async getBuildStatus(): Promise<BuildMobileStatus> {
    const { data } = await apiClient.get<BuildMobileStatus>('/admin/dev/build-mobile/status');
    return data;
  },
};

// ─── Legal docs (privacy / terms) ─────────────────────────────────────────
export type LegalDocSlug = 'privacy' | 'terms';
export type LegalLang = 'es' | 'en';

export interface LegalDocument {
  doc_slug: LegalDocSlug;
  lang: LegalLang;
  content_html: string;
  updated_at: string;
  updated_by: string | null;
}

export const legalApi = {
  async get(slug: LegalDocSlug, lang: LegalLang): Promise<LegalDocument> {
    const { data } = await apiClient.get<LegalDocument>(`/admin/legal/${slug}/${lang}`);
    return data;
  },
  async update(slug: LegalDocSlug, lang: LegalLang, content_html: string): Promise<LegalDocument> {
    const { data } = await apiClient.put<LegalDocument>(
      `/admin/legal/${slug}/${lang}`,
      { content_html },
    );
    return data;
  },
};

// Re-export del cliente axios por si algún componente legacy lo necesita.
export { apiClient };
export default apiClient;
